/** Pitched voices: bass, pad, and the three arp timbres a record can use. */

import { env } from './envelope.ts';
import type { VoiceBus } from './graph.ts';

export function bass({ ctx, music }: VoiceBus, when: number, freq: number, dur: number): void {
  const o = ctx.createOscillator();
  const sub = ctx.createOscillator();
  const f = ctx.createBiquadFilter();
  const g = ctx.createGain();
  o.type = 'sawtooth';
  o.frequency.value = freq;
  sub.type = 'sine';
  sub.frequency.value = freq / 2;
  f.type = 'lowpass';
  f.Q.value = 5;
  f.frequency.setValueAtTime(Math.min(freq * 7, 1400), when);
  f.frequency.exponentialRampToValueAtTime(Math.max(freq * 1.4, 90), when + dur * 0.9);
  g.gain.setValueAtTime(0.0001, when);
  g.gain.exponentialRampToValueAtTime(0.3, when + 0.012);
  g.gain.exponentialRampToValueAtTime(0.0001, when + dur);
  o.connect(f);
  sub.connect(f);
  f.connect(g).connect(music);
  o.start(when);
  sub.start(when);
  o.stop(when + dur + 0.05);
  sub.stop(when + dur + 0.05);
}

export function pad(
  { ctx, music, verb }: VoiceBus,
  when: number,
  freqs: readonly number[],
  dur: number,
  amp = 0.17,
): void {
  const g = ctx.createGain();
  const f = ctx.createBiquadFilter();
  f.type = 'lowpass';
  f.Q.value = 0.6;
  f.frequency.setValueAtTime(520, when);
  f.frequency.linearRampToValueAtTime(1750, when + dur * 0.45);
  f.frequency.linearRampToValueAtTime(600, when + dur);
  g.gain.setValueAtTime(0.0001, when);
  g.gain.linearRampToValueAtTime(amp, when + dur * 0.28);
  g.gain.linearRampToValueAtTime(amp * 0.8, when + dur * 0.7);
  g.gain.exponentialRampToValueAtTime(0.0001, when + dur);
  f.connect(g);
  g.connect(music);
  g.connect(verb);

  for (const fr of freqs) {
    for (const detune of [-5, 5]) {
      const o = ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = fr * 2 ** (detune / 1200);
      const vg = ctx.createGain();
      vg.gain.value = 1 / (freqs.length * 2);
      o.connect(vg).connect(f);
      o.start(when);
      o.stop(when + dur + 0.1);
    }
  }
}

export function pluck(
  { ctx, music, verb }: VoiceBus,
  when: number,
  freq: number,
  dur: number,
): void {
  const o = ctx.createOscillator();
  const harmonic = ctx.createOscillator();
  const f = ctx.createBiquadFilter();
  const g = ctx.createGain();
  o.type = 'triangle';
  o.frequency.value = freq;
  harmonic.type = 'square';
  harmonic.frequency.value = freq * 2.001;
  const hg = ctx.createGain();
  hg.gain.value = 0.14;
  f.type = 'lowpass';
  f.Q.value = 2;
  f.frequency.setValueAtTime(freq * 8, when);
  f.frequency.exponentialRampToValueAtTime(freq * 1.6, when + dur);
  env(g, when, 0.006, dur, 0.17);
  o.connect(f);
  harmonic.connect(hg).connect(f);
  f.connect(g);
  g.connect(music);
  g.connect(verb);
  o.start(when);
  harmonic.start(when);
  o.stop(when + dur + 0.05);
  harmonic.stop(when + dur + 0.05);
}

export function saw({ ctx, music, verb }: VoiceBus, when: number, freq: number, dur: number): void {
  const o = ctx.createOscillator();
  const f = ctx.createBiquadFilter();
  const g = ctx.createGain();
  o.type = 'sawtooth';
  o.frequency.value = freq;
  f.type = 'lowpass';
  f.Q.value = 7;
  f.frequency.setValueAtTime(freq * 6, when);
  f.frequency.exponentialRampToValueAtTime(freq * 1.2, when + dur);
  env(g, when, 0.005, dur, 0.13);
  o.connect(f).connect(g);
  g.connect(music);
  g.connect(verb);
  o.start(when);
  o.stop(when + dur + 0.05);
}

export function bell(
  { ctx, music, verb }: VoiceBus,
  when: number,
  freq: number,
  dur: number,
): void {
  const o = ctx.createOscillator();
  const g = ctx.createGain();
  o.type = 'sine';
  o.frequency.value = freq;
  env(g, when, 0.01, dur, 0.11);
  o.connect(g);
  g.connect(music);
  g.connect(verb);
  o.start(when);
  o.stop(when + dur + 0.05);
}
